
import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useRequisicoes } from '@/hooks/useRequisicoes';
import { Solicitacao } from '@/types/solicitacao';
import { NewRequisicaoDialog } from './NewRequisicaoDialog';
import { EditRequisicaoDialog } from './EditRequisicaoDialog';
import RequisicoesStatsCards from './RequisicoesStatsCards';
import RequisicoesTable from './RequisicoesTable';
import { RequisicoesFilters, RequisicoesFiltersState } from './RequisicoesFilters';
import { exportFilteredRequisicoesToCSV } from '@/utils/csvExport';

const RequisicoesManagement = () => {
  const { requisicoes, isLoading } = useRequisicoes();
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [editingRequisicao, setEditingRequisicao] = useState<Solicitacao | null>(null);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filters, setFilters] = useState<RequisicoesFiltersState>({
    grupo: '',
    cliente: '',
    status: [],
    solicitante: '',
    sla: '',
    atendente: '',
    numero: '',
  });

  // Aplicar filtros às requisições
  const filteredRequisicoes = useMemo(() => {
    if (!requisicoes) return [];

    return requisicoes.filter((req) => {
      // Filtro por número
      if (filters.numero && !req.numero?.toLowerCase().includes(filters.numero.toLowerCase())) {
        return false;
      }

      // Filtro por grupo
      if (filters.grupo && req.grupo_responsavel?.name !== filters.grupo) {
        return false;
      }

      // Filtro por cliente
      if (filters.cliente && req.cliente?.name !== filters.cliente) {
        return false;
      }

      // Filtro por status (multi-select)
      if (filters.status.length > 0 && !filters.status.includes(req.status)) {
        return false;
      }

      // Filtro por solicitante
      if (filters.solicitante && req.solicitante?.name !== filters.solicitante) {
        return false;
      }

      // Filtro por atendente
      if (filters.atendente && req.atendente?.name !== filters.atendente) {
        return false;
      }

      return true;
    });
  }, [requisicoes, filters]);

  // Funções para controle dos filtros
  const handleClearFilters = () => {
    setFilters({
      grupo: '',
      cliente: '',
      status: [],
      solicitante: '',
      sla: '',
      atendente: '',
      numero: '',
    });
  };

  const handleExportCSV = () => {
    exportFilteredRequisicoesToCSV(filteredRequisicoes, filteredRequisicoes.length);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando requisições...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Requisições</h1>
          <span className="text-sm text-muted-foreground">
            ({filteredRequisicoes.length} de {requisicoes.length} registros)
          </span>
        </div>
        <Button onClick={() => setIsNewDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Requisição
        </Button>
      </div>

      <RequisicoesStatsCards requisicoes={filteredRequisicoes} />

      <RequisicoesFilters
        filters={filters}
        onFiltersChange={setFilters}
        onClearFilters={handleClearFilters}
        onExportCSV={handleExportCSV}
        requisicoes={requisicoes || []}
        isVisible={filtersVisible}
        onToggleVisibility={() => setFiltersVisible(!filtersVisible)}
      />

      <RequisicoesTable 
        requisicoes={filteredRequisicoes}
        onEditRequisicao={setEditingRequisicao}
        onNewRequisicao={() => setIsNewDialogOpen(true)}
      />

      <NewRequisicaoDialog
        isOpen={isNewDialogOpen}
        onClose={() => setIsNewDialogOpen(false)}
      />

      {editingRequisicao && (
        <EditRequisicaoDialog
          requisicao={editingRequisicao}
          isOpen={!!editingRequisicao}
          onClose={() => setEditingRequisicao(null)}
        />
      )}
    </div>
  );
};

export default RequisicoesManagement;
